/**
 * Sliders for the home pages:
 *  - Rail: a row of cards that scrolls sideways (swipe on phones, arrows on
 *    desktop), used for categories and product rows.
 *  - BannerSlider: the slides at the top of a page (admin "Slides", or
 *    built-in slides when there are none), moving every few seconds, pausing
 *    on hover or touch and staying still for people who ask for reduced motion.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function Rail({ title, eyebrow, action, children, testid, className = "" }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });
  const update = () => {
    const el = ref.current;
    if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  };
  useEffect(() => { update(); }, [children]);
  const move = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.85, behavior: "smooth" });
  return (
    <section className={`max-w-7xl mx-auto px-4 sm:px-6 ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-end justify-between gap-4 mb-4">
          <div>
            {eyebrow && <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal">{eyebrow}</div>}
            {title && <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">{title}</h2>}
          </div>
          <div className="flex items-center gap-2">
            {action}
            <div className="hidden sm:flex gap-1">
              {[[-1, ChevronLeft, edge.start, "Back"], [1, ChevronRight, edge.end, "More"]].map(([d, Icon, off, label]) => (
                <button key={d} type="button" aria-label={label} disabled={off} onClick={() => move(d)}
                  className="h-9 w-9 rounded-full border border-stone-300 bg-white flex items-center justify-center disabled:opacity-30 hover:border-stoneWarm">
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div ref={ref} onScroll={update}
        className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
    </section>
  );
}

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// The colour panel behind a slide's words (admin "Colour behind the words").
// Each pair passes contrast for text on the panel.
export const SLIDE_TONES = {
  marigold: { panel: "bg-[#FFE3B8] text-stoneWarm", btn: "bg-stoneWarm text-white", dot: "bg-stoneWarm" },
  teal: { panel: "bg-[#0F6E68] text-white", btn: "bg-white text-[#0F6E68]", dot: "bg-white" },
  leaf: { panel: "bg-[#3F6B2A] text-white", btn: "bg-white text-[#3F6B2A]", dot: "bg-white" },
  clay: { panel: "bg-[#B4472A] text-white", btn: "bg-white text-[#B4472A]", dot: "bg-white" },
  charcoal: { panel: "bg-stoneWarm text-white", btn: "bg-marigold text-stoneWarm", dot: "bg-white" },
};

/** The slides at the top of a page. The words sit on their own colour panel,
 *  never on the photo, so any picture stays readable: panel left and photo
 *  right on computers and tablets, photo on top and words below on phones.
 *  The photo is cropped around the admin's focus point at every width.
 *  slides: [{id, title, subtitle, eyebrow, image, imagePhone, focus: [x, y],
 *  tone, cta, to | href, art}] — `art` (no photo) fills the photo side. */
export function BannerSlider({ slides, testid = "banner-slider", className = "" }) {
  const [i, setI] = useState(0);
  const [hold, setHold] = useState(false);
  const n = slides.length;
  useEffect(() => {
    if (n < 2 || hold || reduced()) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n, hold]);
  useEffect(() => { if (i >= n) setI(0); }, [n, i]);
  // Swipe on phones.
  const start = useRef(null);
  if (!n) return null;
  const go = (d) => setI((x) => (x + d + n) % n);
  return (
    <div className={`relative overflow-hidden rounded-[1.75rem] ${className}`} data-testid={testid} aria-roledescription="carousel"
      onMouseEnter={() => setHold(true)} onMouseLeave={() => setHold(false)} onFocus={() => setHold(true)} onBlur={() => setHold(false)}
      onTouchStart={(e) => { start.current = e.touches[0].clientX; setHold(true); }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (start.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); setHold(false); }}>
      <div className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ transform: `translateX(-${i * 100}%)` }}>
        {slides.map((s, k) => {
          const t = SLIDE_TONES[s.tone] || SLIDE_TONES.teal;
          const pos = `${s.focus?.[0] ?? 50}% ${s.focus?.[1] ?? 50}%`;
          const body = (
            <div className="flex flex-col sm:flex-row h-full sm:h-[17rem] lg:h-[19rem]">
              <div className="relative order-1 sm:order-2 aspect-[16/10] sm:aspect-auto sm:flex-1 overflow-hidden bg-stone-200">
                {s.image ? (
                  <picture>
                    {s.imagePhone && <source media="(max-width: 640px)" srcSet={s.imagePhone} />}
                    <img src={s.image} alt="" loading={k ? "lazy" : "eager"} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: pos }} />
                  </picture>
                ) : <div className={`absolute inset-0 ${t.panel} brightness-110`}>{s.art}</div>}
              </div>
              <div className={`order-2 sm:order-1 sm:w-[44%] shrink-0 flex flex-col justify-center gap-1.5 p-5 pb-12 sm:p-8 lg:p-10 ${t.panel}`}>
                {s.eyebrow && <div className="text-[11px] uppercase tracking-[0.18em] font-bold opacity-85">{s.eyebrow}</div>}
                <div className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight">{s.title}</div>
                {s.subtitle && <p className="text-sm sm:text-base opacity-90 max-w-md">{s.subtitle}</p>}
                {s.cta && <span className={`self-start inline-flex mt-3 rounded-full px-5 py-2.5 text-sm font-bold ${t.btn}`}>{s.cta}</span>}
              </div>
            </div>
          );
          return (
            <div key={s.id || k} className="w-full shrink-0" aria-hidden={k !== i} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${n}`} data-testid={`${testid}-slide`}>
              {s.href ? <a href={s.href} tabIndex={k === i ? 0 : -1} className="block h-full">{body}</a>
                : s.to ? <Link to={s.to} tabIndex={k === i ? 0 : -1} className="block h-full">{body}</Link> : body}
            </div>
          );
        })}
      </div>
      {n > 1 && (<>
        {/* Dots sit in the colour panel's corner; arrows on the photo's corner. */}
        <div className="absolute bottom-4 left-5 sm:left-8 lg:left-10 flex gap-1.5">
          {slides.map((s, k) => {
            const t = SLIDE_TONES[slides[i].tone] || SLIDE_TONES.teal;
            return (
              <button key={s.id || k} type="button" aria-label={`Show slide ${k + 1}`} aria-current={k === i} onClick={() => setI(k)}
                className={`h-2 rounded-full transition-all ${t.dot} ${k === i ? "w-6" : "w-2 opacity-40"}`} />
            );
          })}
        </div>
        <div className="hidden sm:flex absolute top-4 right-4 gap-2">
          <button type="button" aria-label="Previous slide" onClick={() => go(-1)}
            className="h-10 w-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" aria-label="Next slide" onClick={() => go(1)}
            className="h-10 w-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </>)}
    </div>
  );
}
